import { getField, updateField } from 'vuex-map-fields'
import BaseStore from './baseStore'
import { getNewId } from '@/utils/id'
import { generateMultipleItemOverview } from '@/store/baseOverview'
import generateBaseImportFromStage from '@/store/baseImportFromStage'
import api from '../api/api'
import BaseDirtyFields from '@/store/baseDirtyFields'

export const namespaced = true

let baseUrl = '/topo_profile_points/'

const baseStore = new BaseStore(baseUrl)
const baseDirtyFields = new BaseDirtyFields()
const baseOverview = generateMultipleItemOverview({
  duplicationCheckFunc: (topoProfilePoint, topoProfileId) => topoProfilePoint.properties.topo_profile === topoProfileId,
})
const baseImportFromStage = generateBaseImportFromStage({ isGeoFeature: true })

const getDefaultState = () => {
  return {
    ...structuredClone(baseStore.state),
    ...structuredClone(baseDirtyFields.state),
    ...structuredClone(baseImportFromStage.state),
    writeLoading: false,
    generateLoading: false,
  }
}

export const state = {
  ...getDefaultState(),
  ...baseOverview.state,
}

export const mutations = {
  ...baseStore.mutations,
  ...baseOverview.mutations,
  ...baseDirtyFields.mutations,
  ...baseImportFromStage.mutations,
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  updateField,
  TOGGLE_GENERATE_LOADING (state) {
    state.generateLoading = !state.generateLoading
  },
  SORT_ITEMS (state) {
    state.items = state.items.sort((a, b) => a.geometry.coordinates[0] - b.geometry.coordinates[0])
  }
}
export const actions = {
  ...baseStore.actions,
  ...baseOverview.actions,
  ...baseDirtyFields.actions,
  ...baseImportFromStage.actions,
  resetState ({ commit }) {
    commit('resetState')
  },
  sortItemsX ({ commit }) {
    commit('SORT_ITEMS')
  },
  async add (context, { item, topoProfileId }) { // TODO refactor
    let id = getNewId(context.state.items, 'id')
    let coordinates = item ? item.geometry.coordinates.slice() : context.state.items.length ? context.state.items[0].geometry.coordinates.slice() : [0, 0]
    let newItem = context.getters.getEmptyItem(id, topoProfileId, coordinates)
    let index = context.state.items.findIndex(existingItem => item && item.id === existingItem.id)
    index += 1
    context.commit('ADD_ITEM_ON_INDEX', { item: newItem, index })
    context.commit('ADD_DIRTY_ITEM', newItem)
  },
  addPointOnClick ({ commit, getters, state }, { coordinates, topoProfileId }) {
    const id = getNewId(state.items, 'id')
    const newItem = getters.getEmptyItem(id, topoProfileId, coordinates)
    const index = state.items.findIndex(item => item.geometry.coordinates[0] >= newItem.geometry.coordinates[0])
    commit('ADD_ITEM_ON_INDEX', { item: newItem, index: index > -1 ? index : state.items.length })
    commit('ADD_DIRTY_ITEM', newItem)
    return newItem
  },
  async generate (context, topoProfileId) {
    context.commit('TOGGLE_GENERATE_LOADING')
    let url = `${baseUrl}generate/`
    try {
      const response = await api.post(url, {
        properties: { topo_profile: topoProfileId }
      })
      context.commit('SET_ITEMS', response.data)
    } catch (e) {
      console.log(e)
    }
    context.commit('TOGGLE_GENERATE_LOADING')
  },
  async importTopoProfileFromStage (
    { commit, dispatch },
    { sourceSchematisationStageId, targetSchematisationStageId, topoProfileId }
  ) {
    try {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
      const topoProfilePoints = await dispatch('importFromStage', {
        import_option: {
          source_stab_schematisation_stage_id: sourceSchematisationStageId,
          target_stab_schematisation_stage_id: targetSchematisationStageId,
        },
        topo_profile: topoProfileId,
      })
      commit('SET_ITEMS', topoProfilePoints)
    } catch (e) {
      console.log(e)
    } finally {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
    }
  },

}

export const getters = {
  getField,
  getEmptyItem: () => (id, topoProfileId, coordinates) => {
    let emptyObj = {}
    emptyObj.id = id
    emptyObj.geometry = {}
    emptyObj.geometry.coordinates = coordinates
    emptyObj.geometry.type = 'Point'
    emptyObj.properties = {}
    emptyObj.properties.name = null
    emptyObj.properties.topo_profile = topoProfileId
    return emptyObj
  },
  getItem: (state) => (id) => {
    return state.items.find(item => item.id === id) || state.itemsOverview.find(item => item.id === id)
  },
  getName: (state) => (value) => {
    if (!state.options) {
      return ''
    }
    let text = state.options.name.choices.find(choice => choice.value === value)
    return text ? text.display_name : ''
  },
  topoProfilePointOptions: (state) => {
    return state.items ? state.items.filter(item => !!(item.properties.name)) : []
  },
  getTopoProfileGeometry: (state, getters) => (stabSchematisationStage = null) => {
    const items = getters.getItemsByStabSchematisationStage(stabSchematisationStage)
    let line = []
    items.forEach((item) => line.push(item.geometry.coordinates))
    return line
  },
  getPlotData: (state, getters, rootState, rootGetters) => (stabSchematisationStage = null) => {
    let storeName = 'topoProfilePoint'
    const items = getters.getItemsByStabSchematisationStage(stabSchematisationStage)
    const topoProfile = rootGetters['topoProfile/getItemByStabSchematisationStage'](stabSchematisationStage)
    let topoProfileSingle = {
      x: [],
      y: [],
      line: { simplify: false },
      mode: 'lines+text+markers',
      type: 'scatter',
      name: null,
      textangle: null,
      textfont: {
        family: 'Roboto',
      },
    }
    topoProfileSingle.x = items.map(
      (item) => item.geometry.coordinates[0]
    )
    topoProfileSingle.y = items.map(
      (item) => item.geometry.coordinates[1]
    )
    topoProfileSingle.id = topoProfile?.id || null
    topoProfileSingle.name = 'topo_profile'
    topoProfileSingle.storeName = storeName
    topoProfileSingle.line = {
      color: 'black',
      width: 3,
    }
    topoProfileSingle.marker = {
      color: 'rgb(255,255,255)',
      size: 7,
      line: {
        color: 'rgb(0,0,0)',
        width: 1,
      },
    }
    return [topoProfileSingle]
  },
  getPlotDataMaxY: (state, getters) => (stabSchematisationStage = null) => {
    return getters.getPlotData(stabSchematisationStage)[0]
      ? Math.max(...getters.getPlotData(stabSchematisationStage)[0].y)
      : null
  },
  getPlotDataMinY: (state, getters) => (stabSchematisationStage = null) => {
    return getters.getPlotData(stabSchematisationStage)[0]
      ? Math.min(...getters.getPlotData(stabSchematisationStage)[0].y)
      : null
  },
  getPlotDataMaxX: (state, getters) => (stabSchematisationStage = null) => {
    return getters.getPlotData(stabSchematisationStage)[0]
      ? Math.max(...getters.getPlotData(stabSchematisationStage)[0].x)
      : null
  },
  getPlotDataMinX: (state, getters) => (stabSchematisationStage = null) => {
    return getters.getPlotData(stabSchematisationStage)[0]
      ? Math.min(...getters.getPlotData(stabSchematisationStage)[0].x)
      : null
  },
  getChartYAxisRange: (state, getters) => (stabSchematisationStage = null) => {
    const DEFAULT_Y_AXIS_RANGE = [-5, 20]
    if (!stabSchematisationStage) {
      return DEFAULT_Y_AXIS_RANGE
    }
    const maxY = getters.getPlotDataMaxY(stabSchematisationStage)
    const minY = getters.getPlotDataMinY(stabSchematisationStage)
    if (maxY === null || minY === null || maxY === -Infinity || minY === Infinity) {
      return DEFAULT_Y_AXIS_RANGE
    }
    const padding = maxY - minY
    return [minY - padding, maxY + padding]
  },
  getItemsByStabSchematisationStage: (state, getters, rootState, rootGetters) => (stabSchematisationStage = null) => {
    if (stabSchematisationStage) {
      const stabSchematisationStageObj = rootGetters['stabSchematisationStage/getStabSchematisationStageById'](stabSchematisationStage.id)
      if (stabSchematisationStageObj) {
        const topoProfile = stabSchematisationStageObj.topoprofile
        return state.itemsOverview.filter(point => point.properties.topo_profile === topoProfile)
      }
    }
    return state.items
  }
}



